import { useEffect, useMemo, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { RcCalendar, fromDay, toDay } from "@/components/RcCalendar";
import { Btn, Field, inputCls } from "@/components/admin/ui";

const STATUS = {
  booked: { label: "Booked", cls: "bg-[#F5EFE6] text-[#0A0A0C]" },
  blocked: { label: "Blacked out", cls: "bg-zinc-600 text-white" },
  pending: { label: "Pending", cls: "bg-[#C4832B] text-[#0A0A0C]" },
};
const SOURCE = { manual: "Added by you", inquiry: "Accepted inquiry", contract: "Signed contract", invoice: "Deposit paid" };

export const CalendarPanel = () => {
  const [entries, setEntries] = useState([]);
  const [day, setDay] = useState(new Date());
  const [label, setLabel] = useState("");

  useEffect(() => {
    api.get("/admin/calendar").then((r) => setEntries(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const byStatus = useMemo(() => {
    const m = { booked: [], blocked: [], pending: [] };
    entries.forEach((e) => m[e.status]?.push(fromDay(e.date)));
    return m;
  }, [entries]);

  const key = toDay(day);
  const dayEntries = entries.filter((e) => e.date === key);

  const add = async (status) => {
    try {
      const { data } = await api.post("/admin/calendar", { date: key, status, label });
      setEntries((xs) => [...xs, data]);
      setLabel("");
      toast.success(`${STATUS[status].label}: ${day.toLocaleDateString()}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/calendar/${id}`);
      setEntries((xs) => xs.filter((x) => x.id !== id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-12" data-testid="calendar-panel">
      <div className="rounded-2xl border border-[#2E2A24] bg-[#121215] p-5 lg:col-span-7">
        <RcCalendar
          tone="dark"
          mode="single"
          selected={day}
          onSelect={(d) => d && setDay(d)}
          modifiers={byStatus}
          modifiersClassNames={{ booked: "!bg-[#F5EFE6] !text-[#0A0A0C]", blocked: "!bg-zinc-600 !text-white", pending: "!bg-[#C4832B] !text-[#0A0A0C]" }}
          classNames={{ months: "flex flex-col items-center" }}
        />
        <div className="mt-4 flex flex-wrap gap-4 text-xs text-zinc-300">
          {Object.entries(STATUS).map(([k, v]) => <span key={k} className="flex items-center gap-2"><span className={`inline-block h-3 w-3 rounded-full ${v.cls}`} />{v.label}</span>)}
        </div>
        <p className="mt-3 text-xs text-zinc-400">Booked and blacked-out dates can't be picked on the website inquiry form. Pending dates stay open until the deposit is paid.</p>
      </div>
      <div className="space-y-4 rounded-2xl border border-[#2E2A24] bg-[#121215] p-5 lg:col-span-5">
        <p className="font-display text-3xl" data-testid="calendar-selected-day">{day.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</p>
        {dayEntries.length ? dayEntries.map((e) => (
          <div key={e.id} className="flex items-start justify-between gap-3 rounded-xl border border-[#2E2A24] p-3" data-testid={`calendar-entry-${e.id}`}>
            <div>
              <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs ${STATUS[e.status]?.cls}`}>{STATUS[e.status]?.label}</span>
              <p className="mt-1 text-sm text-[#F5EFE6]">{e.label || "—"}</p>
              <p className="text-xs text-zinc-400">{SOURCE[e.source] ?? e.source}</p>
            </div>
            <button onClick={() => remove(e.id)} className="text-zinc-400 hover:text-red-400" aria-label="Remove" data-testid={`calendar-remove-${e.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        )) : <p className="text-sm text-zinc-400">This date is open.</p>}
        <div className="border-t border-[#2E2A24] pt-4">
          <Field label="Note (optional)"><input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Family vacation, Conference" className={inputCls} data-testid="calendar-label-input" /></Field>
          <div className="mt-3 flex flex-wrap gap-2">
            <Btn variant="primary" onClick={() => add("blocked")} data-testid="calendar-block-btn">Black out date</Btn>
            <Btn onClick={() => add("booked")} data-testid="calendar-book-btn">Mark booked</Btn>
            <Btn onClick={() => add("pending")} data-testid="calendar-pending-btn">Mark pending</Btn>
          </div>
        </div>
      </div>
    </div>
  );
};
